import { CircularProgress, InputAdornment, Stack, Typography, useTheme } from "@mui/material";
import { GenericDialog } from "src/components/generic-dialog";
import { useInformationPageInvoiceHistorySectionT } from "src/translation-hooks/information-page-invoice-history-section";
import { RootState } from "src/store";
import { connect } from "react-redux";
import { invoiceHistoryActions } from "../../invoice-history-store";
import { useEffect, useMemo, useState } from "react";
import { FormText, IconButton } from "@itk/mui-components";
import { useForm } from "@itk/react-form";
import { FormType } from "./invoice-lines-dialog-types";
import { genericTranslationsK, useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { GridColDef } from "@mui/x-data-grid-premium";
import { DataGridWithIdentity, DekoxIcon } from "src/components";
import { useNavigate } from "react-router";
import { ROUTES_URL } from "src/utils/constants";
import { hasValue, NullGuid, useExceptFirstEffect } from "@itk/typescript-utils";
import { useUserSettings } from "src/hooks";
import { thousandsSeparator } from "src/utils";
import { applicationActions } from "src/components/application-store";
import { IDENTITY_SETTINGS_KEYS } from "src/utils/types/identity-settings-types";

const mapStateToProps = (state: RootState) => ({
  invoiceLinesDialogState: state.pageReducer.informationPageReducer.invoiceHistoryReducer.invoiceLinesDialogState,
  invoiceLinesState: state.pageReducer.informationPageReducer.invoiceHistoryReducer.invoiceLinesState,
  creditNoteLinesDialogState: state.pageReducer.informationPageReducer.invoiceHistoryReducer.creditNoteLinesDialogState,
  identitySettings: state.generalReducer.applicationReducer.identitySettings,
  invoicesState: state.pageReducer.informationPageReducer.invoiceHistoryReducer.invoicesState
});

const ownActions = {
  setInvoiceLinesDialog: invoiceHistoryActions.setInvoiceLinesDialog,
  getInvoiceLinesRequest: invoiceHistoryActions.getInvoiceLinesRequest,
  setCreditNoteLinesDialog: invoiceHistoryActions.setCreditNoteLinesDialog,
  setIdentitySettingsRequest: applicationActions.setIdentitySettingsRequest
};

type Props = typeof ownActions & ReturnType<typeof mapStateToProps>;

const InvoiceLinesDialogBase = (props: Props) => {
  const {
    invoiceLinesDialogState,
    setInvoiceLinesDialog,
    getInvoiceLinesRequest,
    invoiceLinesState,
    creditNoteLinesDialogState,
    setCreditNoteLinesDialog,
    identitySettings,
    setIdentitySettingsRequest,
    invoicesState
  } = props;

  const theme = useTheme();
  const navigate = useNavigate();
  const { language } = useUserSettings();
  const locale = language === "da" ? "da-dk" : "en";

  const activeDialogState = invoiceLinesDialogState ?? creditNoteLinesDialogState;
  const selectedInvoiceId = activeDialogState?.selectedInvoiceId;

  const informationPageInvoiceHistorySectionT = useInformationPageInvoiceHistorySectionT();
  const genT = useGenericTranslationsT();

  const [debouncedSearch, setDebouncedSearch] = useState("");

  const initialState = useMemo<FormType>(() => {
    return {
      wildcardSearch: ""
    };
  }, []);

  const { formState, formController, getValues } = useForm(initialState);

  useExceptFirstEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(getValues()?.wildcardSearch || "");
    }, 500); // debounce delay

    return () => clearTimeout(handler);
  }, [formState.wildcardSearch.value]);

  useEffect(() => {
    if (selectedInvoiceId) {
      getInvoiceLinesRequest(selectedInvoiceId, { wildCardSearch: debouncedSearch });
    }
  }, [selectedInvoiceId, debouncedSearch]);

  const columns: GridColDef[] = [
    { field: "productNumber", headerName: informationPageInvoiceHistorySectionT.productNumber, flex: 1.5 },
    { field: "productDescription", headerName: informationPageInvoiceHistorySectionT.productDescription, flex: 2 },
    { field: "description", headerName: informationPageInvoiceHistorySectionT.description, flex: 2 },
    { field: "quantity", headerName: informationPageInvoiceHistorySectionT.quantity, flex: 1.5 },
    {
      field: "linePriceExVAT",
      headerName: informationPageInvoiceHistorySectionT.totalAmount,
      flex: 1.5,
      headerAlign: "right",
      align: "right",
      renderCell: params => <Typography>{thousandsSeparator(params.row.linePriceExVAT, locale)}</Typography>
    },
    {
      field: "actions",
      headerName: "",
      flex: 0,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) =>
        hasValue(row.productId) &&
        row.productId !== NullGuid && (
          <Stack direction="row" justifyContent="flex-end" flex={1}>
            <IconButton
              k={genericTranslationsK.dynamicValue}
              onClick={() => {
                handleNavigateToProductDetails(row.productId);
              }}
            >
              <DekoxIcon icon="arrow-right" />
            </IconButton>
          </Stack>
        )
    }
  ];

  const handleNavigateToProductDetails = (productId: string) => {
    if (!productId || productId === NullGuid) return;

    navigate(ROUTES_URL.PRODUCT_DETAILS.build(productId));
  };

  const handleClose = () => {
    if (invoiceLinesDialogState) {
      setInvoiceLinesDialog(undefined);
    } else if (creditNoteLinesDialogState) {
      setCreditNoteLinesDialog(undefined);
    }
  };

  const selectedInvoice = useMemo(() => {
    return invoicesState.loading === "has-loaded" ? invoicesState.data.data.find(invoice => invoice.navInvoiceId === selectedInvoiceId) : undefined;
  }, [invoicesState, selectedInvoiceId]);

  return (
    <GenericDialog
      headerTitle={`${informationPageInvoiceHistorySectionT.invoiceLines} ${selectedInvoice?.navInvoiceNumber ? `(${selectedInvoice.navInvoiceNumber})` : ""}`}
      onClose={handleClose}
      dialogProps={{ maxWidth: "lg" }}
    >
      <Stack gap={2} height="80vh">
        <Stack direction="row">
          <FormText
            field="search"
            t={genT}
            k={genericTranslationsK}
            formFieldState={formState.wildcardSearch}
            formFieldController={formController.wildcardSearch}
            sx={{
              "& .MuiInputBase-root": {
                minWidth: 500
              }
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <DekoxIcon icon="search" />
                  </InputAdornment>
                )
              }
            }}
          />
        </Stack>

        {invoiceLinesState.loading === "is-loading" && (
          <Stack flex={1} justifyContent="center" alignItems="center">
            <CircularProgress />
          </Stack>
        )}
        {invoiceLinesState.loading === "has-loaded" && (
          <DataGridWithIdentity
            dataGridSettings={identitySettings[IDENTITY_SETTINGS_KEYS.INVOICE_LINE_DATA_GRID]}
            handleSaveSettings={value => {
              setIdentitySettingsRequest(IDENTITY_SETTINGS_KEYS.INVOICE_LINE_DATA_GRID, JSON.stringify(value));
            }}
            rows={invoiceLinesState.data}
            columns={columns}
            getRowId={row => row.navInvoiceLineId}
            onRowDoubleClick={row => {
              handleNavigateToProductDetails(row.row.productId);
            }}
            onCellKeyDown={(rowEvent, e) => {
              if (e.key === "Enter" && rowEvent.colDef.field !== "actions") handleNavigateToProductDetails(rowEvent.row.productId);
            }}
            hideFooter
            sx={{
              overflow: "auto",
              color: theme.palette.customColors.darkGrey,
              background: theme.palette.customColors.white,
              borderRadius: 1,
              fontWeight: "normal",
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: theme.typography.fontWeightBold,
                fontSize: theme.typography.body2.fontSize,
                color: theme.palette.customColors.darkGrey
              },
              "& .MuiDataGrid-virtualScroller": {
                overflowX: "hidden",
                fontSize: theme.typography.subtitle1.fontSize
              }
            }}
          />
        )}
      </Stack>
    </GenericDialog>
  );
};

export const InvoiceLinesDialog = connect(mapStateToProps, ownActions)(InvoiceLinesDialogBase);
